import {
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useRef,
} from "react";
import { Link } from "react-router";
import { cx } from "../cx";
import { type Placement, Popover } from "../Popover";
import styles from "./Menu.module.css";

export interface MenuProps {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  placement?: Placement;
  offset?: number;
  crossOffset?: number;
  /** Panel width in px; Clockify menus are fixed-width (140 entry menu, 203 workspace menu, 300 profile). */
  width?: number;
  /** Row height in px: 40 for in-page kebab menus, 43 for the top-bar menus. */
  rowHeight?: number;
  radius?: "legacy" | "profile";
  className?: string;
  "aria-label"?: string;
  children: ReactNode;
}

const ITEM_SELECTOR = '[role="menuitem"]:not([aria-disabled="true"])';

export function Menu({
  open,
  onClose,
  anchorRef,
  placement = "bottom-end",
  offset = 4,
  crossOffset,
  width,
  rowHeight = 40,
  radius = "legacy",
  className,
  children,
  ...rest
}: MenuProps) {
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(listRef.current?.querySelectorAll<HTMLElement>(ITEM_SELECTOR) ?? []);
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    let next: number | null = null;
    if (e.key === "ArrowDown") next = i < 0 ? 0 : (i + 1) % items.length;
    else if (e.key === "ArrowUp") next = i <= 0 ? items.length - 1 : i - 1;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = items.length - 1;
    if (next !== null) {
      e.preventDefault();
      items[next]?.focus();
    }
  }, []);

  return (
    <Popover
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      placement={placement}
      offset={offset}
      crossOffset={crossOffset}
      className={cx(styles.menu, radius === "profile" && styles.profile, className)}
      style={{ width, "--menu-row-height": `${rowHeight}px` } as CSSProperties}
    >
      {/* Clicking any item closes the menu, like Clockify's ngbDropdown autoClose. */}
      <div
        ref={listRef}
        role="menu"
        tabIndex={-1}
        aria-label={rest["aria-label"]}
        onKeyDown={onKeyDown}
        onClick={(e: MouseEvent) => {
          if ((e.target as HTMLElement).closest(ITEM_SELECTOR)) onClose();
        }}
      >
        {children}
      </div>
    </Popover>
  );
}

interface ItemBase {
  children: ReactNode;
  icon?: ReactNode;
  danger?: boolean;
  disabled?: boolean;
  /** Highlights the current page in nav flyouts. */
  active?: boolean;
  className?: string;
}

export interface MenuItemProps extends ItemBase {
  onSelect?: () => void;
}

export function MenuItem({ children, icon, danger, disabled, active, onSelect, className }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      aria-disabled={disabled || undefined}
      tabIndex={-1}
      className={cx(
        styles.item,
        danger && styles.danger,
        disabled && styles.disabled,
        active && styles.active,
        className,
      )}
      onClick={(e) => {
        if (disabled) {
          e.stopPropagation();
          return;
        }
        onSelect?.();
      }}
    >
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </button>
  );
}

export interface MenuLinkProps extends ItemBase {
  to: string;
  /** External links open in a new tab. */
  external?: boolean;
}

export function MenuLink({ children, icon, danger, active, to, external, className }: MenuLinkProps) {
  const cls = cx(styles.item, danger && styles.danger, active && styles.active, className);
  if (external) {
    return (
      <a role="menuitem" tabIndex={-1} className={cls} href={to} target="_blank" rel="noreferrer">
        {icon && <span className={styles.icon}>{icon}</span>}
        {children}
      </a>
    );
  }
  return (
    <Link role="menuitem" tabIndex={-1} className={cls} to={to} aria-current={active ? "page" : undefined}>
      {icon && <span className={styles.icon}>{icon}</span>}
      {children}
    </Link>
  );
}

export function MenuHeader({ children }: { children: ReactNode }) {
  return <div className={styles.header}>{children}</div>;
}

export function MenuDivider() {
  return <hr className={styles.divider} />;
}
